import type { ReactNode } from "react";
import { Checkbox } from "@a2z/ui";

type MethodCardProps = {
  logo: ReactNode;
  title: ReactNode;
  helper?: ReactNode;
  /** Selection state. Omit `onToggle` + pass `locked`/`badge` for non-interactive cards. */
  checked?: boolean;
  onToggle?: (next: boolean) => void;
  /** Disabled-teaser styling (e.g. the Paymob "coming soon" card). */
  disabled?: boolean;
  /** Trailing element in the header (badge, status chip). */
  trailing?: ReactNode;
  /** Revealed body, shown only while `checked`. */
  children?: ReactNode;
};

// Shared card shell for every payment method. Calm header (logo + title +
// helper + selection control); a height-animated reveal for configuration
// fields that only opens when the method is enabled. Mirrors the reveal
// pattern shipped in DepositSettingsForm so the whole section reads as one
// consistent component. RTL-safe (logical properties, no hardcoded hex).
export function MethodCard({
  logo,
  title,
  helper,
  checked = false,
  onToggle,
  disabled = false,
  trailing,
  children,
}: MethodCardProps) {
  const interactive = Boolean(onToggle) && !disabled;
  const open = interactive && checked && Boolean(children);

  const header = (
    <div
      className={[
        "flex items-center gap-3 px-4 py-3.5",
        interactive ? "cursor-pointer" : "",
        disabled ? "opacity-70" : "",
      ].join(" ")}
    >
      {interactive ? (
        <Checkbox
          size="sm"
          checked={checked}
          onChange={(e) => onToggle?.(e.target.checked)}
        />
      ) : (
        <span className="inline-block h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      {logo}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-[color:var(--text-primary)]">
            {title}
          </span>
          {trailing}
        </div>
        {helper ? (
          <div className="mt-0.5 text-xs leading-relaxed text-[color:var(--text-muted)]">
            {helper}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      className={[
        "overflow-hidden rounded-xl border bg-[color:var(--surface)] transition-colors",
        checked && interactive
          ? "border-[color:var(--accent-500)]"
          : "border-[color:var(--border-light)]",
      ].join(" ")}
    >
      {interactive ? (
        <label className="block">{header}</label>
      ) : (
        header
      )}

      <div
        className={[
          "grid transition-all duration-300 ease-in-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
      >
        <div className="overflow-hidden">
          <div className="border-t border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-4 py-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
